import { FormBuilder, FormControl, FormControlOptions, FormGroup, ValidatorFn } from '@angular/forms';
import { Directive, Injectable, Input, NgModule } from '@angular/core';
import { DataBagService } from './data-bag.service';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import * as _ from 'lodash';
import { DatePipe } from '@angular/common';

/**
 * This is the description of the interface
 *
 * @interface FormControlConfig
 * @property {string} name - The name used for filter
 * @property {string | number | boolean} initialValue - The default value of the filter
 * @property {ValidatorFn | ValidatorFn[] | FormControlOptions | null} validators - Used to define validation for formControl
 * @property {string} group - Used to create formControls in formGroup
 * @property {string} separator - Used to define how we will distribute filter values in the API Url
 * @property {boolean} multiple - Used when you want to set formControl value as array
 */

export interface FormControlConfig {
	name: string;
	initialValue?: string | number | boolean;
	validators?: ValidatorFn | ValidatorFn[] | FormControlOptions | null;
	group?: FormControlConfig[];
	separator?: string;
	multiple?: boolean;
}

interface CustomParams {
	[key: string]: string;
}

@Injectable({
	providedIn: 'root',
})
export class NewFilters {
	constructor(
		protected formBuilder: FormBuilder,
		protected api: DataBagService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected datePipe: DatePipe,
	) {}
	filtersFormGroup: FormGroup = new FormGroup({});
	controls: FormControlConfig[];
	url = '';
	private readonly expression: RegExp = /[ąćęłńóśźż]/gi;
	private readonly replacements: Record<string, string> = {
		ą: 'a',
		ć: 'c',
		ę: 'e',
		ł: 'l',
		ń: 'n',
		ó: 'o',
		ś: 's',
		ź: 'z',
		ż: 'z',
		Ą: 'A',
		Ć: 'C',
		Ę: 'E',
		Ł: 'L',
		Ń: 'N',
		Ó: 'O',
		Ś: 'S',
		Ź: 'Z',
		Ż: 'Z',
	};
	parameters: CustomParams;

	createFormGroup() {
		this.filtersFormGroup = this.setFormGroupControls(this.controls);
		this.setQueryParams(this.buildQueryParams);
	}

	setFormGroupControls(controlConfigs: FormControlConfig[]): FormGroup {
		const formGroup: FormGroup = new FormGroup({});
		controlConfigs.forEach((config) => {
			const { name, initialValue, validators, group, multiple } = config;
			const queryParams = this.route.snapshot.queryParams[name];
			if (group) {
				formGroup.addControl(name, this.setFormGroupControls(group));
			} else {
				const controlValue = initialValue ?? this.retrieveValueFromParam(queryParams);
				formGroup.addControl(
					name,
					new FormControl(
						multiple && controlValue ? [controlValue].flatMap((value) => value) : controlValue,
						validators || [],
					),
				);
			}
		});
		return formGroup;
	}

	private retrieveValueFromParam(queryParam: string) {
		if (!queryParam) {
			return '';
		}
		if (queryParam === 'true') {
			return true;
		}
		if (queryParam === 'false') {
			return false;
		}
		if (queryParam.includes(',')) {
			const queryArray = queryParam.split(',');
			return queryArray.map((query) => (/^\d+$/.test(query) ? +query : query));
		}
		if (/^\d+$/.test(queryParam)) {
			return +queryParam;
		}
		return queryParam;
	}

	clearFilters() {
		this.setQueryParams({});
		this.filtersFormGroup.reset();
	}

	get buildFilterUrl(): string {
		let params = '?';
		Object.keys(this.filtersFormGroup.controls).forEach((key) => {
			const value = this.filtersFormGroup.get(key).value;
			if (value && typeof value === 'object') {
				if (Date.parse(value) && !Array.isArray(value)) {
					params += `${key}=${this.datePipe.transform(new Date(value), 'YYYY-MM-dd')}&`;
				} else {
					const combineStr = this.combineStrings(value, key);
					if (combineStr) params += `${key}=${combineStr}&`;
				}
			} else if (value) params += `${key}=${value}&`;
		});
		for (const key in this.parameters) {
			params += `${key}=${this.parameters[key]}&`;
		}
		return this.replacePolishLetters(params).toLowerCase();
	}

	private replacePolishLetters(text: any): string {
		if (typeof text !== 'string') return text;
		return text.replace(this.expression, (letter) => this.replacements[letter]);
	}

	protected get buildQueryParams(): Params {
		let queryParams = {};
		for (const controlName in this.filtersFormGroup.controls) {
			const control = this.filtersFormGroup.get(controlName);
			if (Array.isArray(control.value)) {
				queryParams[controlName] = control.value.join(',');
			} else if (control.value && typeof control.value === 'object') {
				if (Date.parse(control.value)) {
					queryParams[controlName] = this.datePipe.transform(new Date(control.value), 'YYYY-MM-dd');
				} else queryParams = { ...queryParams, ...control.value };
			} else if (control.value !== '') queryParams[controlName] = control.value;
		}
		return _.mapValues(queryParams, (v) => (v === '' ? null : v));
	}

	private combineStrings(value: object, key: string): string {
		let str = '';
		const separator = this.controls.find((config) => config.name === key).separator ?? ',';
		Object.keys(value).forEach((v) => {
			if (value[v]) str = str.concat(value[v] + separator);
		});
		if (str.endsWith(separator)) {
			str = str.slice(0, -separator.length);
		}
		return str;
	}

	protected setQueryParams(queryParams: Params) {
		const navigationExtras: NavigationExtras = {
			queryParams,
			replaceUrl: true,
		};
		this.router.navigate([], navigationExtras);
	}

	async submit(): Promise<any> {
		if (!this.url) throw new Error('Url property is required!');
		this.filtersFormGroup.markAllAsTouched();
		if (this.filtersFormGroup.invalid) return;
		return new Promise((resolve, reject) => {
			this.api.get(this.url + this.buildFilterUrl, (result, error) => {
				if (result) {
					this.setQueryParams(this.buildQueryParams);
					resolve(result);
				} else reject(error);
			});
		});
	}
}
